import type { BotConfig, ExchangeAccount, User } from "@investhinks/api-client";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardBody, CardHeader, UncontrolledTooltip } from "reactstrap";

import getStoploss from "../../utils/getStoploss";
import isVipOrZeroPriceTier from "../../utils/isVipOrZeroPriceTier";
import { getStrategyName } from "../../utils/risks";

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  exchangeAccount?: ExchangeAccount | null;
  bot?: BotConfig | null;
  user?: User | null;
  onEditCredentials: () => void;
  onEditBot: () => void;
  onChangePayment: () => void;
  onToggleBot: () => void;
  onHideAccount: () => void;
  isAdmin?: boolean;
  canEdit?: boolean;
  onMoveAccount: () => void;
  onCancelSubscription: () => void;
}

const SettingsDrawer = ({
  isOpen,
  onClose,
  // Data
  exchangeAccount,
  bot,
  user,
  // Actions
  onEditCredentials,
  onEditBot,
  onChangePayment,
  onToggleBot,
  onHideAccount,
  // Permissions
  isAdmin = false,
  canEdit = false, // Can edit credentials, bot config, payment (owner only)
  onMoveAccount,
  onCancelSubscription,
}: SettingsDrawerProps) => {
  const { t } = useTranslation("dashboard", { keyPrefix: "exchangeAccount.settingsDrawer" });

  const { exchange, defaultLeverage, accountId } = exchangeAccount || {};
  const { strategy, userConfig, subscription, disabled } = bot || {};
  const { risk, maxActiveBalance, sailingModeThreshold, maxLeverage } = userConfig || {};

  const stopLoss = getStoploss(
    strategy ? { name: strategy.name, configOptions: strategy.configOptions } : null,
    userConfig ? { risk: userConfig.risk, advancedStopLoss: userConfig.advancedStopLoss } : undefined
  );
  const hasPaymentError = !isVipOrZeroPriceTier(user) && ["failed", "unpaid", "failed_escalated"].includes(subscription?.pm_status ?? "");
  const paymentLast4 = subscription?.pm_paymentMethod?.pm_details?.last4;

  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // The drawer used to stay mounted and merely slide off-screen, so every
  // control inside it (edit credentials, pause, archive, cancel subscription)
  // stayed in the tab order behind the page.
  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`settings-drawer-backdrop ${isOpen ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`settings-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("title")}
        aria-hidden={!isOpen}
        {...(isOpen ? {} : { inert: "" })}
      >
        {/* Header */}
        <div className="settings-drawer-header">
          <h4 className="mb-0">
            <i className="fas fa-cog mr-2" aria-hidden="true" />
            {t("title")}
          </h4>
          <Button
            innerRef={closeButtonRef}
            color="link"
            className="text-dark p-0"
            onClick={onClose}
            aria-label={t("close")}
          >
            <i className="fas fa-times fa-lg" aria-hidden="true" />
          </Button>
        </div>

        {/* Content */}
        <div className="settings-drawer-content">
          {/* Account Section */}
          <Card className="mb-3">
            <CardHeader className="py-2">
              <h6 className="mb-0 text-uppercase text-muted">{t("accountSection")}</h6>
            </CardHeader>
            <CardBody>
              {exchangeAccount ? (
                <>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("broker")}</span>
                    <span className="font-weight-bold">{exchange?.name || "--"}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("accountId")}</span>
                    <span className="font-weight-bold">{accountId || "--"}</span>
                  </div>
                  <div className={`d-flex justify-content-between ${isAdmin ? "" : "mb-3"}`}>
                    <span className="text-muted">{t("leverage")}</span>
                    <span className="font-weight-bold">1:{defaultLeverage || "--"}</span>
                  </div>
                  {canEdit && (
                    <Button color="secondary" size="sm" block onClick={onEditCredentials} className="mt-3">
                      <i className="fas fa-key mr-2" />
                      {t("editCredentials")}
                    </Button>
                  )}
                </>
              ) : (
                !isAdmin && (
                  <div className="text-center text-muted py-3">
                    <i className="fas fa-link fa-2x mb-2" />
                    <p className="mb-2">{t("noAccount")}</p>
                    <Button color="primary" size="sm" onClick={onEditCredentials}>
                      {t("connectAccount")}
                    </Button>
                  </div>
                )
              )}
            </CardBody>
          </Card>

          {/* Bot Configuration Section */}
          <Card className="mb-3">
            <CardHeader className="py-2">
              <h6 className="mb-0 text-uppercase text-muted">{t("botSection")}</h6>
            </CardHeader>
            <CardBody>
              {bot ? (
                <>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("strategy")}</span>
                    <span className="font-weight-bold">{getStrategyName(strategy, risk)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("stopLoss")}</span>
                    <span className="font-weight-bold">{stopLoss ? `${(stopLoss * 100).toFixed(0)}%` : t("off")}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("maxLeverage")}</span>
                    <span className="font-weight-bold">1:{maxLeverage || "1000"}</span>
                  </div>
                  {!!strategy?.configOptions?.maxActiveBalance && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">{t("mab")}</span>
                      <span className="font-weight-bold">
                        {maxActiveBalance ? `${maxActiveBalance.toFixed(0)}` : t("off")}
                      </span>
                    </div>
                  )}
                  {!!strategy?.configOptions?.sailingMode && (
                    <div className={`d-flex justify-content-between ${isAdmin ? "mb-2" : "mb-3"}`}>
                      <span className="text-muted">{t("sailingMode")}</span>
                      <span className="font-weight-bold">
                        {sailingModeThreshold ? `${sailingModeThreshold}%` : t("off")}
                      </span>
                    </div>
                  )}
                  {canEdit && (
                    <Button color="secondary" size="sm" block onClick={onEditBot}>
                      <i className="fas fa-sliders-h mr-2" />
                      {t("editConfiguration")}
                    </Button>
                  )}
                </>
              ) : (
                !isAdmin && (
                  <div className="text-center text-muted py-3">
                    <i className="fas fa-robot fa-2x mb-2" />
                    <p className="mb-2">{t("noBot")}</p>
                    <Button color="primary" size="sm" onClick={onEditBot} disabled={!exchangeAccount}>
                      {t("connectBot")}
                    </Button>
                  </div>
                )
              )}
            </CardBody>
          </Card>

          {/* Payment Section - Only for account owners */}
          {bot && !isVipOrZeroPriceTier(user) && canEdit && (
            <Card className="mb-3">
              <CardHeader className="py-2">
                <h6 className="mb-0 text-uppercase text-muted">{t("paymentSection")}</h6>
              </CardHeader>
              <CardBody>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">{t("card")}</span>
                  <span className="font-weight-bold">{paymentLast4 ? `**** ${paymentLast4}` : "--"}</span>
                </div>
                <div className={`d-flex justify-content-between ${isAdmin ? "" : "mb-3"}`}>
                  <span className="text-muted">{t("status")}</span>
                  <Badge color={hasPaymentError ? "danger" : "success"}>
                    {hasPaymentError ? t("paymentError") : t("active")}
                  </Badge>
                </div>
                <Button
                  color={hasPaymentError ? "danger" : "secondary"}
                  size="sm"
                  block
                  onClick={onChangePayment}
                  className="mt-3"
                >
                  <i className="fas fa-credit-card mr-2" />
                  {t("changePayment")}
                </Button>
              </CardBody>
            </Card>
          )}

          {/* Bot Status */}
          {bot && (
            <Card className="mb-3">
              <CardHeader className="py-2">
                <h6 className="mb-0 text-uppercase text-muted">{t("botStatus")}</h6>
              </CardHeader>
              <CardBody>
                <div
                  className={`d-flex justify-content-between align-items-center ${canEdit || isAdmin ? "mb-3" : ""}`}
                >
                  <span className="text-muted">{t("currentStatus")}</span>
                  <Badge color={disabled ? "warning" : "success"} className="px-3 py-2">
                    {disabled ? t("paused") : t("running")}
                  </Badge>
                </div>
                {(canEdit || isAdmin) && (
                  <Button color={disabled ? "success" : "warning"} size="sm" block onClick={onToggleBot}>
                    <i className={`fas fa-${disabled ? "play" : "pause"} mr-2`} />
                    {disabled ? t("resumeBot") : t("pauseBot")}
                  </Button>
                )}
              </CardBody>
            </Card>
          )}

          {/* Admin Actions - Only show for admins */}
          {isAdmin && exchangeAccount && (
            <Card className="mb-3 border-info">
              <CardHeader className="py-2 bg-info text-white">
                <h6 className="mb-0 text-uppercase">
                  <i className="fas fa-user-shield mr-2" />
                  {t("adminSection")}
                </h6>
              </CardHeader>
              <CardBody>
                <Button color="info" size="sm" block onClick={onMoveAccount}>
                  <i className="fas fa-exchange-alt mr-2" />
                  {t("moveAccount")}
                </Button>
                {subscription?.pm_status === "active" && (
                  <div className="mt-2">
                    <Button color="danger" size="sm" block onClick={onCancelSubscription}>
                      <i className="fas fa-ban mr-2" />
                      {t("cancelSubscription")}
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Danger Zone - For account owners and admins */}
          {exchangeAccount && (canEdit || isAdmin) && (
            <Card className="border-danger">
              <CardHeader className="py-2 bg-danger text-white">
                <h6 className="mb-0 text-uppercase">
                  <i className="fas fa-exclamation-triangle mr-2" />
                  {t("dangerZone")}
                </h6>
              </CardHeader>
              <CardBody>
                <div id="hide-account-btn-wrapper" className="mb-2">
                  <Button
                    color="secondary"
                    size="sm"
                    block
                    outline={!bot || !!disabled}
                    onClick={onHideAccount}
                    disabled={!!bot && !disabled}
                    style={bot && !disabled ? { pointerEvents: "none", opacity: 0.5 } : {}}
                  >
                    <i className="fas fa-eye-slash mr-2" />
                    {exchangeAccount?.hidden ? t("showAccount") : t("hideAccount")}
                  </Button>
                </div>
                {bot && !disabled && (
                  <UncontrolledTooltip target="hide-account-btn-wrapper" placement="top">
                    {t("archiveTooltip")}
                  </UncontrolledTooltip>
                )}
                <p className="text-muted small mb-0">{t("dangerZoneDescription")}</p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Styles */}
      <style>{`
        .settings-drawer-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1040;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
        }
        .settings-drawer-backdrop.open {
          opacity: 1;
          visibility: visible;
        }
        .settings-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          /* 100vw includes the scrollbar gutter, so the drawer was slightly
             wider than the space its children were laid out for. */
          width: min(380px, 100%);
          background: #f8f9fa;
          z-index: 1050;
          transform: translateX(100%);
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
          box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
        }
        .settings-drawer.open {
          transform: translateX(0);
        }
        .settings-drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.25rem;
          background: white;
          border-bottom: 1px solid #dee2e6;
        }
        .settings-drawer-content {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
          padding-bottom: calc(1rem + env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
};

export default SettingsDrawer;
